import { PaperPlaneTilt, User, UserPlus } from '@phosphor-icons/react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { messages as messagesApi, users as usersApi } from '../../api/endpoints';
import { ScreenError, ScreenLoader, SubScreen } from '../../components/layout/Screens';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { IconButton } from '../../components/ui/Button';
import { useAsync } from '../../hooks/useAsync';
import { t } from '../../i18n';

/**
 * Mes amis.
 *
 * La liste ne sert pas qu'a compter : c'est de la qu'on ouvre un profil
 * ou qu'on lance une conversation, sans passer par la recherche. Ajouter
 * quelqu'un, en revanche, se fait ailleurs — l'ecran des demandes et des
 * suggestions existe deja, on y renvoie plutot que de le recopier.
 */
export function FriendsListScreen() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync(() => usersApi.friendList(), [], 'friend-list');
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * Ouvre le fil avec cet ami, en le creant s'il n'existe pas encore.
   * Le meme chemin que depuis sa fiche : c'est la couche donnee qui sait
   * si une conversation existe deja.
   */
  async function write(userId: string) {
    setBusy(userId);
    setFailure(null);
    try {
      const conversation = await messagesApi.openDirect(userId);
      navigate(`/app/messages/${conversation.id}`);
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : t.carte.ecran.impossible);
      setBusy(null);
    }
  }

  return (
    <SubScreen
      title={t.profil.commun.mesAmis}
      action={
        <IconButton
          label={t.profil.commun.ajouterAmis}
          variant="ghost"
          size={34}
          onClick={() => navigate('/app/messages/friends')}
        >
          <UserPlus size={20} />
        </IconButton>
      }
    >
      {loading && <ScreenLoader />}
      {error && <ScreenError message={error} onRetry={reload} />}
      {failure && <p className="field__error">{failure}</p>}

      {data && data.length === 0 && (
        <EmptyState
          icon={<User size={30} />}
          title={t.profil.amis.aucun}
          hint={t.profil.amis.aucunAide}
          action={{
            label: t.profil.amis.trouver,
            onClick: () => navigate('/app/messages/friends'),
          }}
        />
      )}

      {/*
        Un seul bloc, des lignes plutot que des cartes : une liste d'amis
        se parcourt du regard, elle n'a pas besoin qu'on detache chaque
        nom. Pas de bio non plus — c'est un annuaire, pas huit profils.
      */}
      {data && data.length > 0 && (
        <div className="card-list">
          {data.map((friend) => (
            <div key={friend.id} className="pr-friend">
              <Link to={`/app/members/${friend.id}`} aria-label={friend.username}>
                <Avatar user={friend} size={36} showOnline />
              </Link>

              <Link to={`/app/members/${friend.id}`} className="pr-friend__name">
                {friend.username}
              </Link>

              {/*
                L'avion posé a meme la ligne : une pastille de plus dans un
                rectangle qui en est deja un n'ajoutait qu'un contour.
              */}
              <button
                type="button"
                className="pr-friend__write"
                aria-label={t.profil.commun.ecrireA(friend.username)}
                disabled={busy === friend.id}
                onClick={() => void write(friend.id)}
              >
                <PaperPlaneTilt className="sens-x" size={19} />
              </button>
            </div>
          ))}
        </div>
      )}
    </SubScreen>
  );
}
